import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { Store } from '@ngrx/store';
import { ENV, Environment, SocketService } from 'lib-trend-core';
import { Subject, fromEvent, merge } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import * as ChatActions from './chat.actions';
import { ChatConnectionStatus } from './chat.state';

/** How long the "Conectado" recovery bar stays visible. */
export const CHAT_CONNECTION_RESTORED_MS = 3_000;

/**
 * How long a first connection may stay silent. Until it elapses the status is
 * `connecting` (no banner — avoids a banner flash on every page load); after
 * it, a still-dead socket is reported honestly as reconnecting/offline.
 */
export const CHAT_CONNECTION_GRACE_MS = 8_000;

/**
 * Internal Chat connection watcher (SPEC-2026-07-internal-chat, T-25 · RNF-08).
 *
 * Single source of the `connectionStatus` ui slice: it folds the socket link
 * state (`SocketService.getConnectedStatus()`) together with the device's own
 * network state (`online`/`offline` window events) into the four states the
 * banner renders, and auto-clears the brief post-recovery flag.
 *
 * Scope note (AC-20): this service only ever dispatches into the chat feature
 * slice. It installs no HTTP interceptor and no route guard, so a dead chat
 * backend or a dead socket can never affect the attendance screens — the chat
 * surfaces are the only consumers of what it publishes.
 *
 * `init()` is idempotent and called by ChatConnectionBannerComponent, i.e. by
 * whichever chat surface (popup or `/chat`) mounts first.
 */
@Injectable({ providedIn: 'root' })
export class ChatConnectionService implements OnDestroy {

  private readonly destroy$ = new Subject<void>();

  private initialized = false;

  /** Locally tracked mirror of the published status (previous-state decisions). */
  private status: ChatConnectionStatus = 'connecting';

  /** The socket linked up at least once in this session. */
  private hasConnected = false;

  /** The silent first-connection window has elapsed. */
  private graceElapsed = false;

  private graceTimer: ReturnType<typeof setTimeout> | null = null;

  private restoredTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly store: Store,
    private readonly socketService: SocketService,
    @Optional() @Inject(ENV) private readonly environment: Environment | null,
  ) { }

  /** Idempotent activation — wires the socket + device network signals. */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Real-time disabled by configuration: there is no link to wait for, so
    // report it once as offline instead of a perpetual "reconnecting".
    if (this.environment && this.environment['socketActived'] !== true) {
      this.publish('offline');
      return;
    }

    this.socketService.getConnectedStatus()
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((connected) => this.evaluate(connected));

    // A device that lost its network reports "connection lost" immediately,
    // without waiting for socket.io to notice the dead transport.
    merge(fromEvent(window, 'online'), fromEvent(window, 'offline'))
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.evaluate(this.socketService.isConnected()));

    this.graceTimer = setTimeout(() => {
      this.graceElapsed = true;
      this.graceTimer = null;
      this.evaluate(this.socketService.isConnected());
    }, CHAT_CONNECTION_GRACE_MS);

    this.evaluate(this.socketService.isConnected());
  }

  ngOnDestroy(): void {
    this.clearTimer('grace');
    this.clearTimer('restored');
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private evaluate(connected: boolean): void {
    if (connected) {
      this.hasConnected = true;
      this.clearTimer('grace');
      this.publish('online');
      return;
    }

    // Silent until we know a link is really expected (first load).
    if (!this.hasConnected && !this.graceElapsed) {
      this.publish('connecting');
      return;
    }

    this.publish(this.isDeviceOffline() ? 'offline' : 'reconnecting');
  }

  private publish(status: ChatConnectionStatus): void {
    if (this.status === status) return;

    const recovered = status === 'online' && (this.status === 'reconnecting' || this.status === 'offline');
    this.status = status;
    this.store.dispatch(ChatActions.setConnectionStatus({ status }));

    if (!recovered) return;

    // The reducer raised `connectionRestored` — hide it again shortly after.
    this.clearTimer('restored');
    this.restoredTimer = setTimeout(() => {
      this.restoredTimer = null;
      this.store.dispatch(ChatActions.clearConnectionRestored());
    }, CHAT_CONNECTION_RESTORED_MS);
  }

  private isDeviceOffline(): boolean {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  }

  private clearTimer(timer: 'grace' | 'restored'): void {
    if (timer === 'grace' && this.graceTimer) {
      clearTimeout(this.graceTimer);
      this.graceTimer = null;
    }
    if (timer === 'restored' && this.restoredTimer) {
      clearTimeout(this.restoredTimer);
      this.restoredTimer = null;
    }
  }
}
